import DataQuery from 'data/DataQuery';
import { ContractualAccounts_ContactPersons, ContractualAccounts_Get, ContractualAccounts_PropertyManager, Customer_PaymentMethods } from 'data/endpoints';
import { useQuery } from 'data/hooks/useQuery';
import { getRouteParams } from 'features/shared/helpers/routeParams';
import React from 'react';
import ContractualAccountFullDTO from '../dtos/ContractualAccountFullDTO';
import ContractualAccountInfoCard from 'features/contractualAccounts/components/ContractualAccountInfoCard';
import NoContentWrapper from 'features/shared/components/noContentWrapper/NoContentWrapper';
import SendingMethodsCard from 'features/contracts/components/SendingMethodsCard';
import { useSelector } from 'react-redux';
import IAppState from 'infrastructure/store/IAppState';
import CustomerState from 'infrastructure/store/customer/CustomerState';
import PaymentMethodDTO from 'features/payments/dtos/PaymentMethodDTO';
import { getQueryParams } from 'features/shared/helpers/queryParams';
import PaymentMethodList from 'features/payments/components/PaymentMethodList';
import ContactPersonDTO from 'features/contractualAccounts/dtos/ContactPersonDTO';
import { ContactPersonType } from 'features/contractualAccounts/enums/contactPersonType';
import PropertyManagerDTO from 'features/contractualAccounts/dtos/PropertyManagerDTO';
import PropertyManagerInfoCard from 'features/contractualAccounts/components/PropertyManagerInfoCard';
import { containsFuelingCommodity } from 'features/contracts/helpers/commodityDetailHelper';

const numberOfShowContacts = 3;

interface Props {
  contractualAccountId: string;
}

const ContractualAccountDetailSection = ({ contractualAccountId }: Props) => {
  // store
  const customer = (useSelector<IAppState, CustomerState>((store) => store.customer)).customer;

  // fetch data
  // contractual account
  const accountQuery: DataQuery = {
    url: ContractualAccounts_Get,
    routeParams: getRouteParams("contractualAccountId", contractualAccountId)
  };
  const { data: account, loadStatus: accountLoadStatus } = useQuery<ContractualAccountFullDTO>(accountQuery, {}, [contractualAccountId]);
  // payment methods
  const paymentMethodsQuery: DataQuery = {
    url: Customer_PaymentMethods,
    routeParams: getRouteParams("customerId", customer.customerId!),
    params: getQueryParams("ContractualAccountId", contractualAccountId)
  };
  const { data: paymentMethods, loadStatus: paymentMethodsLoadStatus } = useQuery<PaymentMethodDTO[]>(paymentMethodsQuery, [], [contractualAccountId, customer]);
  // contact persons
  const contactPersonsQuery: DataQuery = {
    url: ContractualAccounts_ContactPersons,
    routeParams: getRouteParams("contractualAccountId", contractualAccountId),
    params: getQueryParams("ContactPersonType", [ContactPersonType.ContactPerson, ContactPersonType.Accounter])
  };
  const { data: contactPersons, loadStatus: contactPersonsLoadStatus } = useQuery<ContactPersonDTO[]>(contactPersonsQuery, [], [contractualAccountId]);
  // property manager
  const propertyManagerQuery: DataQuery = {
    url: ContractualAccounts_PropertyManager,
    routeParams: getRouteParams("contractualAccountId", contractualAccountId)
  };
  const { data: propertyManager, loadStatus: propertyManagerLoadStatus } = useQuery<PropertyManagerDTO>(propertyManagerQuery, {}, [contractualAccountId]);

  // helpers
  const getPersonContact = (type: ContactPersonType): ContactPersonDTO => {
    let person = contactPersons.find(x => x.contactPersonType === type);
    return person ? person : {};
  }

  // render
  return (
    <NoContentWrapper
      data={account}
      loadStatus={[
        accountLoadStatus,
        paymentMethodsLoadStatus,
        contactPersonsLoadStatus,
        propertyManagerLoadStatus
      ]}
      noContentAsAlert
    >
      <ContractualAccountInfoCard 
        contractualAccount={account} 
        person={getPersonContact("ContactPerson")}
      />

      <SendingMethodsCard
        mailMethods={account.mailMethods || []}
        contacts={account.billingContacts || []}
        address={account.billingAddress!}
        contactsCount={numberOfShowContacts}
      />

      <PaymentMethodList 
        paymentMethods={paymentMethods} 
        contactPerson={getPersonContact("Accounter")}
        enabledAdvanceInvoicing={
          !containsFuelingCommodity(account.commodityDetails!)
        }
      />
     
      <NoContentWrapper
        data={propertyManager}
        loadStatus={propertyManagerLoadStatus}
        noContentRender={() => <></>}
      >
        <PropertyManagerInfoCard manager={propertyManager} />
      </NoContentWrapper>
    </NoContentWrapper>
  )
}

export default ContractualAccountDetailSection;
